import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardContent,
  CircularProgress,
  capitalize,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { useState } from "react";
import { useApiQuery } from "../../hooks";
import { useApiMutation } from "../../hooks/use-api-mutation";
import {
  type ExternalProviderConfig,
  type ExternalProviderConfigDataEmail,
  type ExternalProviderConfigDataSlack,
  isExternalProviderConfigEmail,
  isExternalProviderConfigSlack,
  type NotificationSettings,
  NotificationSettingsConfigScopeList,
  NotificationSettingsConfigTypeList,
  TestRunStatusList,
} from "../../types";

export const NotificationsTab: React.FC = () => {
  const {
    data: settings,
    isLoading: isLoadingSettings,
    isRefetching: isRefetchingSettings,
    refetch: refetchSettings,
  } = useApiQuery<NotificationSettings>("/notification_settings", {
    refetchInterval: false,
  });

  const { data: externalConfigs, refetch } = useApiQuery<{
    data: ExternalProviderConfig[];
  }>("/external_provider_configs");

  // Check if Slack OAuth is connected
  const { data: slackOAuthStatus } = useApiQuery<{
    connected: boolean;
    provider: string;
    integration_id?: string;
    metadata?: { team_name?: string; team_id?: string };
  }>("/oauth/slack/status", {
    refetchInterval: false,
  });

  const { enqueueSnackbar } = useSnackbar();
  const [showNotificationToken, setShowNotificationToken] = useState(false);
  const [newNotificationAnchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const newNotificationOpen = Boolean(newNotificationAnchorEl);

  // State for editing existing config
  const [editConfig, setEditConfig] = useState<ExternalProviderConfig | null>(null);

  // State for creating new config
  const [newConfigDialogOpen, setNewConfigDialogOpen] = useState(false);
  const [newConfigType, setNewConfigType] = useState<"slack" | "email" | null>(null);
  const [slackToken, setSlackToken] = useState("");
  const [slackChannel, setSlackChannel] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [useOAuth, setUseOAuth] = useState(true); // Prefer OAuth by default

  // State for deleting config
  const [configToDelete, setConfigToDelete] = useState<ExternalProviderConfig | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { mutate: updateSettings, isPending: isUpdatingSettings } = useApiMutation<
    Pick<NotificationSettings, "enabled" | "onboarding_notifications_enabled" | "config">,
    NotificationSettings
  >("PUT", "/notification_settings/{id}", {
    onSuccess: () => {
      enqueueSnackbar("Notification settings updated", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      refetchSettings();
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(`Could not update notification settings: ${error.message}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  // Mutations for creating and updating configs
  const { mutate: createConfig, isPending: isCreating } = useApiMutation<
    {
      provider: "slack" | "email";
      enabled: boolean;
      config: { token?: string; channel?: string; email?: string };
      oauth_integration_id?: string;
    },
    ExternalProviderConfig
  >("POST", "/external_provider_configs", {
    onSuccess: () => {
      enqueueSnackbar("Notification provider created", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      // Close dialog and reset state
      setNewConfigDialogOpen(false);
      setNewConfigType(null);
      setSlackToken("");
      setSlackChannel("");
      setEmailAddress("");

      // Refetch configs
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(`Error creating notification provider: ${error.message}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const { mutate: updateConfig, isPending: isUpdating } = useApiMutation<
    ExternalProviderConfigDataSlack | ExternalProviderConfigDataEmail,
    ExternalProviderConfig
  >("PUT", "/external_provider_configs/{id}", {
    onSuccess: () => {
      enqueueSnackbar("Notification provider updated", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      // Reset edit state
      setEditConfig(null);

      // Refetch configs
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(`Error updating notification provider: ${error.message}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const { mutate: deleteConfig, isPending: isDeleting } = useApiMutation<void, void>(
    "DELETE",
    "/external_provider_configs/{id}",
    {
      onSuccess: () => {
        enqueueSnackbar("Notification provider deleted", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });

        // Close dialog and reset state
        setDeleteConfirmOpen(false);
        setConfigToDelete(null);

        // Refetch configs
        refetch();
      },
      onError: (error) => {
        enqueueSnackbar(`Error deleting notification provider: ${error.message}`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        setDeleteConfirmOpen(false);
      },
    },
  );

  const handleClickNewNotification = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseNewNotification = () => {
    setAnchorEl(null);
  };

  const handleSelectNewConfigType = (type: "slack" | "email") => {
    setNewConfigType(type);
    // Set useOAuth based on whether OAuth is connected
    if (type === "slack") {
      setUseOAuth(slackOAuthStatus?.connected ?? false);
    }
    setNewConfigDialogOpen(true);
    handleCloseNewNotification();
  };

  const handleCreateConfig = () => {
    if (newConfigType === "slack") {
      // Validate channel is always required
      if (!slackChannel) {
        enqueueSnackbar("Please enter a channel", {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        return;
      }

      // Validate token for manual mode
      if (!useOAuth && !slackToken) {
        enqueueSnackbar("Please enter an OAuth token", {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        return;
      }

      // Create OAuth-linked or manual token config
      if (useOAuth && slackOAuthStatus?.connected && slackOAuthStatus.integration_id) {
        createConfig({
          provider: "slack",
          enabled: true,
          oauth_integration_id: slackOAuthStatus.integration_id,
          config: {
            channel: slackChannel,
          },
        });
      } else {
        createConfig({
          provider: "slack",
          enabled: true,
          config: {
            token: slackToken,
            channel: slackChannel,
          },
        });
      }
    } else if (newConfigType === "email") {
      if (!emailAddress) {
        enqueueSnackbar("Please fill in all fields", {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        return;
      }

      createConfig({
        provider: "email",
        enabled: true,
        config: {
          email: emailAddress,
        },
      });
    }
  };

  const handleUpdateConfig = () => {
    if (!editConfig) return;

    if (isExternalProviderConfigSlack(editConfig)) {
      updateConfig({
        provider: "slack",
        config: editConfig.config,
        enabled: editConfig.enabled,
        _url: `/external_provider_configs/${editConfig.id}`,
      });
    } else if (isExternalProviderConfigEmail(editConfig)) {
      updateConfig({
        provider: "email",
        enabled: editConfig.enabled,
        config: editConfig.config,
        _url: `/external_provider_configs/${editConfig.id}`,
      });
    }
  };

  const handleEditSlackConfig = (config: ExternalProviderConfig, field: string, value: string) => {
    if (isExternalProviderConfigSlack(config)) {
      setEditConfig({
        ...config,
        config: {
          ...config.config,
          [field]: value,
        },
      });
    }
  };

  const handleEditEmailConfig = (config: ExternalProviderConfig, field: string, value: string) => {
    if (isExternalProviderConfigEmail(config)) {
      setEditConfig({
        ...config,
        config: {
          ...config.config,
          [field]: value,
        },
      });
    }
  };

  const handleDeleteClick = (config: ExternalProviderConfig) => {
    setConfigToDelete(config);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setConfigToDelete(null);
  };

  const handleDeleteConfirm = () => {
    if (configToDelete) {
      deleteConfig({
        _url: `/external_provider_configs/${configToDelete.id}`,
      });
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Typography variant="h5" component="div">
            Notifications
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {settings && (
              <Tooltip title={settings.enabled ? "Disable notifications" : "Enable notifications"}>
                <Switch
                  checked={settings.enabled}
                  disabled={isUpdatingSettings}
                  onChange={(e) => {
                    updateSettings({
                      ...settings,
                      enabled: e.target.checked,
                      _url: `/notification_settings/${settings.id}`,
                    });
                  }}
                  color="primary"
                />
              </Tooltip>
            )}
            <Button
              id="new-notification-button"
              aria-label="New notification"
              aria-controls={newNotificationOpen ? "new-notification-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={newNotificationOpen ? "true" : undefined}
              onClick={handleClickNewNotification}
              disabled={!settings?.enabled || isUpdatingSettings || isCreating}
              loading={isUpdatingSettings || isCreating}
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
            >
              New integration
            </Button>
          </Stack>
          <Menu
            id="basic-menu"
            aria-labelledby="new-notification-button"
            anchorEl={newNotificationAnchorEl}
            open={newNotificationOpen}
            onClose={handleCloseNewNotification}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiMenu-paper": {
                marginTop: 1,
                minWidth: 150,
              },
            }}
          >
            <MenuItem aria-label="Slack" onClick={() => handleSelectNewConfigType("slack")}>
              Slack
            </MenuItem>
            <MenuItem aria-label="Email" onClick={() => handleSelectNewConfigType("email")}>
              Email
            </MenuItem>
          </Menu>
        </Stack>
        <Typography variant="body2" mt={1} color="text.secondary">
          Setup notifications to receive alerts when a suite or a test run ends.
        </Typography>

        {/* Onboarding Notifications Section */}
        {settings && (
          <Card variant="outlined" sx={{ marginTop: 3, marginBottom: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="h6" component="div">
                    Onboarding Notifications
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Receive reminders to help you complete the onboarding process
                  </Typography>
                </Stack>
                <Tooltip
                  title={
                    settings.onboarding_notifications_enabled
                      ? "Disable onboarding notifications"
                      : "Enable onboarding notifications"
                  }
                >
                  <Switch
                    checked={settings.onboarding_notifications_enabled ?? false}
                    disabled={isUpdatingSettings}
                    onChange={(e) => {
                      updateSettings({
                        ...settings,
                        onboarding_notifications_enabled: e.target.checked,
                        _url: `/notification_settings/${settings.id}`,
                      });
                    }}
                    color="primary"
                  />
                </Tooltip>
              </Stack>
            </CardContent>
          </Card>
        )}
        <Stack direction="row" spacing={1} mt={4} mb={4} alignItems="center">
          {settings ? (
            <Stack direction="column" spacing={2} alignItems="stretch" width="100%">
              <Autocomplete
                fullWidth
                id="notification-settings-scope-select"
                loading={isRefetchingSettings || isLoadingSettings || isUpdatingSettings}
                disabled={isRefetchingSettings || isLoadingSettings || isUpdatingSettings}
                options={NotificationSettingsConfigScopeList}
                getOptionLabel={(option) => {
                  switch (option) {
                    case "test":
                      return "Test Runs";
                    case "test_suite":
                      return "Test Suite Runs";
                    case "all":
                      return "All Test and Suite Runs";
                    default:
                      return option;
                  }
                }}
                value={settings.config.scope}
                onChange={(_, value) => {
                  updateSettings({
                    ...settings,
                    config: {
                      ...settings.config,
                      scope: value ?? "all", // Default to "all" if no value is selected
                    },
                    _url: `/notification_settings/${settings.id}`,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    multiline
                    fullWidth
                    label="Type of runs to notify"
                    placeholder="Scope"
                  />
                )}
              />
              <Autocomplete
                multiple
                fullWidth
                id="notification-settings-on_status-select"
                loading={isRefetchingSettings || isLoadingSettings || isUpdatingSettings}
                disabled={isRefetchingSettings || isLoadingSettings || isUpdatingSettings}
                options={TestRunStatusList}
                getOptionLabel={(option) => {
                  return capitalize(option.replace(/_/g, " "));
                }}
                value={settings.config.on_status}
                onChange={(_, value) => {
                  updateSettings({
                    ...settings,
                    config: {
                      ...settings.config,
                      on_status: value,
                    },
                    _url: `/notification_settings/${settings.id}`,
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} multiline fullWidth label="Notify on specific status" />
                )}
              />
              <Autocomplete
                fullWidth
                id="notification-settings-type-select"
                loading={isRefetchingSettings || isLoadingSettings || isUpdatingSettings}
                disabled={isRefetchingSettings || isLoadingSettings || isUpdatingSettings}
                options={NotificationSettingsConfigTypeList}
                getOptionLabel={(option) => {
                  return capitalize(option.replace("_", " "));
                }}
                value={settings.config.type}
                onChange={(_, value) => {
                  updateSettings({
                    ...settings,
                    config: {
                      ...settings.config,
                      type: value ?? "instant",
                    },
                    _url: `/notification_settings/${settings.id}`,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    multiline
                    fullWidth
                    label="Type of notification"
                    placeholder="Type"
                  />
                )}
              />
            </Stack>
          ) : (
            <Stack direction="column" spacing={2} alignItems="center" justifyContent="center">
              {isLoadingSettings ? (
                <Stack direction="column" spacing={2} alignItems="center" justifyContent="center">
                  <Typography variant="body1">Loading notification settings...</Typography>
                  <CircularProgress size={24} color="inherit" />
                </Stack>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No notification settings found. Please create a notification provider.
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
        {externalConfigs?.data.map((config) => (
          <Card key={config.id} variant="outlined" sx={{ marginTop: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Typography variant="h6" component="div">
                  {capitalize(config.provider)}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Tooltip title={`${config.enabled ? "Disable" : "Enable"} integration`}>
                    <Switch
                      checked={config.enabled}
                      disabled={isUpdating || !settings?.enabled}
                      onChange={(e) => {
                        updateConfig({
                          ...config,
                          enabled: e.target.checked,
                          _url: `/external_provider_configs/${config.id}`,
                        });
                      }}
                      color="primary"
                    />
                  </Tooltip>
                  <Tooltip title="Delete integration">
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={`Delete ${config.id}`}
                      onClick={() => handleDeleteClick(config)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              {isExternalProviderConfigSlack(config) && (
                <Stack
                  direction="column"
                  spacing={2}
                  mt={2}
                  alignItems="stretch"
                  justifyContent="space-between"
                >
                  {/* OAuth-linked config: Show workspace info instead of token */}
                  {config.oauth_integration_id ? (
                    <TextField
                      sx={{ marginTop: 1 }}
                      variant="outlined"
                      size="small"
                      label="Integration"
                      value="Connected via OAuth"
                      fullWidth
                      disabled
                      helperText="Securely connected via OAuth integration"
                    />
                  ) : (
                    /* Legacy config: Show token field */
                    <TextField
                      sx={{ marginTop: 1 }}
                      variant="outlined"
                      size="small"
                      label="OAuth Token"
                      placeholder="xoxb-***"
                      value={
                        editConfig &&
                        editConfig.id === config.id &&
                        isExternalProviderConfigSlack(editConfig)
                          ? editConfig.config.token
                          : config.config.token
                      }
                      onChange={(e) => handleEditSlackConfig(config, "token", e.target.value)}
                      fullWidth
                      type={showNotificationToken ? "text" : "password"}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="start">
                              <IconButton
                                size="small"
                                aria-label={showNotificationToken ? "Unmask Token" : "Mask Token"}
                                onClick={() => setShowNotificationToken((prev) => !prev)}
                              >
                                {showNotificationToken ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  )}
                  <TextField
                    sx={{ marginTop: 1 }}
                    variant="outlined"
                    label="Default Slack Channel"
                    size="small"
                    placeholder="#channel-name or ID"
                    value={
                      editConfig &&
                      editConfig.id === config.id &&
                      isExternalProviderConfigSlack(editConfig)
                        ? editConfig.config.channel
                        : config.config.channel
                    }
                    onChange={(e) => handleEditSlackConfig(config, "channel", e.target.value)}
                    fullWidth
                  />
                  <Alert severity="info" sx={{ marginTop: 1 }}>
                    This is the default channel for all notifications. You can override this on a
                    per-test-suite basis by configuring a channel in each test suite's settings.
                  </Alert>
                  {editConfig && editConfig.id === config.id && (
                    <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1}>
                      <Button size="small" onClick={() => setEditConfig(null)}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleUpdateConfig}
                        disabled={isUpdating}
                      >
                        Save
                      </Button>
                    </Stack>
                  )}
                </Stack>
              )}
              {isExternalProviderConfigEmail(config) && (
                <Stack
                  direction="column"
                  spacing={2}
                  mt={2}
                  alignItems="stretch"
                  justifyContent="space-between"
                >
                  <TextField
                    sx={{ marginTop: 1 }}
                    variant="outlined"
                    size="small"
                    label="Email"
                    placeholder="your@name.com"
                    value={
                      editConfig &&
                      editConfig.id === config.id &&
                      isExternalProviderConfigEmail(editConfig)
                        ? editConfig.config.email
                        : config.config.email
                    }
                    onChange={(e) => handleEditEmailConfig(config, "email", e.target.value)}
                    fullWidth
                  />
                  {editConfig && editConfig.id === config.id && (
                    <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1}>
                      <Button size="small" onClick={() => setEditConfig(null)}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleUpdateConfig}
                        disabled={isUpdating}
                      >
                        Save
                      </Button>
                    </Stack>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        ))}

        {/* Dialog for creating new notification provider */}
        <Dialog
          open={newConfigDialogOpen}
          onClose={() => setNewConfigDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Create new {newConfigType === "slack" ? "Slack" : "Email"} notification
          </DialogTitle>
          <DialogContent>
            {newConfigType === "slack" && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                {slackOAuthStatus?.connected && (
                  <FormControlLabel
                    control={
                      <Switch checked={useOAuth} onChange={(e) => setUseOAuth(e.target.checked)} />
                    }
                    label={
                      useOAuth
                        ? `Use OAuth integration${slackOAuthStatus.metadata?.team_name ? ` (${slackOAuthStatus.metadata.team_name})` : ""}`
                        : "Use manual token"
                    }
                  />
                )}
                {!useOAuth && (
                  <TextField
                    label="OAuth Token"
                    variant="outlined"
                    size="small"
                    placeholder="xoxb-***"
                    value={slackToken}
                    onChange={(e) => setSlackToken(e.target.value)}
                    fullWidth
                    type={showNotificationToken ? "text" : "password"}
                    required
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="start">
                            <IconButton
                              size="small"
                              aria-label={showNotificationToken ? "Unmask Token" : "Mask Token"}
                              onClick={() => setShowNotificationToken((prev) => !prev)}
                            >
                              {showNotificationToken ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
                <TextField
                  label="Default Slack Channel"
                  variant="outlined"
                  size="small"
                  placeholder="#channel-name or ID"
                  value={slackChannel}
                  onChange={(e) => setSlackChannel(e.target.value)}
                  fullWidth
                  required
                />
              </Stack>
            )}
            {newConfigType === "email" && (
              <TextField
                label="Email"
                variant="outlined"
                size="small"
                placeholder="your@name.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                fullWidth
                required
                sx={{ mt: 1 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewConfigDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateConfig} disabled={isCreating} variant="contained">
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for confirming deletion */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Confirm Notification Provider Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete the {configToDelete?.provider} notification provider?
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" autoFocus disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};
